import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, FileCheck2, ShieldCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Navbar, goToContact } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { COMPLIANCE_DETAILS, COMPLIANCE_LEGAL_CONTENT, COMPLIANCE_IMAGES } from "@/data/content";

export default function ComplianceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const detail = COMPLIANCE_DETAILS[slug] || COMPLIANCE_DETAILS["ai-act"];
  const legal = COMPLIANCE_LEGAL_CONTENT[slug] || COMPLIANCE_LEGAL_CONTENT["ai-act"];

  return (
    <div className="bg-[#14163C] min-h-screen">
      <Seo title={`${detail.code}: cumplimiento y evidencias | Cibermedida`} description={detail.intro} />
      <Navbar />
      <main className="pt-32 pb-24">
        <section className="max-w-7xl mx-auto px-5 sm:px-8">
          <Link to="/cumplimiento" className="inline-flex items-center gap-2 text-sm text-[#B9C3EC] hover:text-[#3FE0F2] transition-colors">
            <ArrowLeft size={16} /> Volver a cumplimiento
          </Link>
          <div className="max-w-4xl mt-12">
            <p className="font-mono-cyber text-xs uppercase tracking-[0.24em] text-[#3FE0F2]">{detail.code} · {detail.eyebrow}</p>
            <h1 className="mt-5 font-heading text-4xl sm:text-6xl font-semibold tracking-tight text-[#EEFBFF] leading-[1.05]">{detail.title}</h1>
            <p className="mt-7 text-xl text-[#B9C3EC] leading-relaxed max-w-3xl">{detail.intro}</p>
            <button onClick={() => goToContact(navigate, `${detail.code}: ${detail.focus}`)} className="mt-9 inline-flex items-center gap-2 text-sm font-semibold text-[#14163C] bg-[#3FE0F2] rounded-full px-7 py-3.5 hover:bg-[#7FEBF8] transition-colors group">
              {detail.cta} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {COMPLIANCE_IMAGES[slug] && (
            <div className="mt-16 relative rounded-3xl overflow-hidden border border-[#3FE0F2]/20">
              <img src={COMPLIANCE_IMAGES[slug]} alt={detail.title} width="1400" height="933" className="w-full h-[260px] sm:h-[380px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#14163C] via-[#14163C]/30 to-transparent" />
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6 mt-12">
            <div className="lg:col-span-2 bg-[#242764] border border-[#3FE0F2]/15 rounded-2xl p-7 sm:p-10">
              <div className="flex items-center gap-3 mb-5"><ShieldCheck className="text-[#06D6A0]" size={24} /><h2 className="font-heading text-2xl font-semibold">Qué resolvemos</h2></div>
              <p className="text-[#B9C3EC] leading-relaxed">{detail.description}</p>
              <h3 className="font-heading text-xl font-semibold mt-9 mb-5">Resultado del acompañamiento</h3>
              <ul className="grid sm:grid-cols-2 gap-4">
                {detail.outcomes.map((item) => <li key={item} className="flex gap-3 text-sm text-[#D8E2FF] leading-relaxed"><CheckCircle2 size={18} className="shrink-0 text-[#06D6A0] mt-0.5" />{item}</li>)}
              </ul>
            </div>
            <aside className="bg-[#1B1E4E] border border-[#3FE0F2]/15 rounded-2xl p-7 h-fit">
              <div className="flex items-center gap-3 mb-6"><FileCheck2 className="text-[#3FE0F2]" size={22} /><h2 className="font-heading text-xl font-semibold">Qué te entregamos</h2></div>
              <ul className="space-y-4">{detail.deliverables.map((item) => <li key={item} className="flex gap-3 text-sm text-[#B9C3EC] leading-relaxed"><span className="text-[#3FE0F2]">/</span>{item}</li>)}</ul>
            </aside>
          </div>

          {detail.extra && (
            <section className="mt-20" data-testid="compliance-extra">
              <p className="font-mono-cyber text-xs uppercase tracking-[0.2em] text-[#3FE0F2]">{detail.extra.kicker}</p>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold text-[#EEFBFF]">{detail.extra.title}</h2>
              <p className="mt-5 max-w-3xl text-[#B9C3EC] leading-relaxed">{detail.extra.intro}</p>
              <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {detail.extra.items.map(([title, text]) => (
                  <div key={title} className="bg-[#242764] border border-[#3FE0F2]/15 rounded-2xl p-6">
                    <h3 className="font-heading text-base font-semibold text-[#3FE0F2]">{title}</h3>
                    <p className="mt-2 text-sm text-[#B9C3EC] leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="mt-20 max-w-5xl">
            <p className="font-mono-cyber text-xs uppercase tracking-[0.2em] text-[#3FE0F2]">La normativa, explicada</p>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold text-[#EEFBFF]">Qué significa {detail.code} en la práctica</h2>
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              <article className="bg-[#1B1E4E] border border-[#3FE0F2]/15 rounded-2xl p-7">
                <h3 className="font-heading text-xl font-semibold mb-3">¿Qué es?</h3>
                <p className="text-sm text-[#B9C3EC] leading-relaxed">{legal.what}</p>
              </article>
              <article className="bg-[#1B1E4E] border border-[#3FE0F2]/15 rounded-2xl p-7">
                <h3 className="font-heading text-xl font-semibold mb-3">¿A quién afecta?</h3>
                <p className="text-sm text-[#B9C3EC] leading-relaxed">{legal.who}</p>
              </article>
            </div>
            <h3 className="font-heading text-2xl font-semibold mt-12 mb-6">Obligaciones y principios esenciales</h3>
            <div className="grid md:grid-cols-2 gap-5">
              {legal.rules.map(([title, text]) => (
                <article key={title} className="border border-[#3FE0F2]/15 rounded-2xl p-6 bg-[#242764]/60">
                  <h4 className="font-heading text-lg font-semibold text-[#EEFBFF]">{title}</h4>
                  <p className="mt-2 text-sm text-[#B9C3EC] leading-relaxed">{text}</p>
                </article>
              ))}
            </div>
            <div className="mt-8 border-l-2 border-[#3FE0F2] bg-[#242764]/50 px-6 py-5">
              <h3 className="font-heading text-lg font-semibold">Vigencia y calendario</h3>
              <p className="mt-2 text-sm text-[#B9C3EC] leading-relaxed">{legal.timeline}</p>
            </div>
            <p className="mt-6 text-xs text-[#94A4D0] leading-relaxed">Información divulgativa, no asesoramiento jurídico. La aplicación depende del sector, el tipo de entidad, el sistema y la normativa nacional vigente. <a href={legal.source} target="_blank" rel="noreferrer" className="text-[#3FE0F2] hover:underline">{legal.sourceLabel} ↗</a></p>
          </section>

          <section className="mt-20 border-t border-[#3FE0F2]/15 pt-12">
            <p className="font-mono-cyber text-xs uppercase tracking-[0.2em] text-[#3FE0F2]">Cómo trabajamos</p>
            <div className="grid md:grid-cols-3 gap-6 mt-7">
              {[['01', 'Entender', 'Revisamos el contexto, los sistemas y las obligaciones que realmente aplican.'], ['02', 'Priorizar', 'Convertimos las brechas en un plan realista con responsables, plazos y evidencias.'], ['03', 'Demostrar', 'Acompañamos la implantación y dejamos un sistema de revisión que tu equipo puede mantener.']].map(([number, title, text]) => <div key={number} className="border-l border-[#3FE0F2]/30 pl-5"><span className="font-mono-cyber text-sm text-[#3FE0F2]">{number}</span><h2 className="font-heading text-xl font-semibold mt-3">{title}</h2><p className="mt-2 text-sm text-[#B9C3EC] leading-relaxed">{text}</p></div>)}
            </div>
          </section>
        </section>
      </main>
      <Footer />
    </div>
  );
}
